import clsx from "clsx";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import {
  Grid,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import EditIcon from "@material-ui/icons/Edit";
import DoneIcon from "@material-ui/icons/Done";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DateRangeIcon from "@material-ui/icons/DateRange";

import FloatingPanel from "./FloatingPanel";
import LayerWithType from "./LayerWithType";
import { FilledTextField } from "../../components/FormElements";

import { FleetSettingsContext, GeofencesContext } from "../../context";
import { GeofencesLayerTypes, GeofencesPanelPositions, GeofencesEditModes } from "../../constants";

const overflowTextStyle = {
  margin: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const useStyles = makeStyles((theme) => ({
  titleWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  titleEditor: {
    position: "relative",
    flexGrow: 1,
    marginRight: theme.spacing(1),
    padding: `${theme.spacing(2)}px 0`,
    overflowX: "hidden",
    "& > div": {
      position: "absolute",
      top: theme.spacing(1),
      margin: 0,
      left: 0,
      visibility: "hidden",
      "& input": { paddingTop: theme.spacing(1) },
    },
  },
  isEditing: {
    "& > h3": { visibility: "hidden" },
    "& > div": { visibility: "visible" },
  },
  overflowText: { ...overflowTextStyle },
  iconsWrapper: {
    marginLeft: "auto",
    whiteSpace: "nowrap",
    visibility: "hidden",
  },
  toggleButtonGroup: {
    display: "flex",
    flexWrap: "wrap",
    margin: `0 0 ${theme.spacing(2)}px ${theme.spacing(1)}px`,
    "& > button": { width: "10em" },
  },
  addButton: { marginBottom: theme.spacing(2) },
  buttonIcon: { marginRight: theme.spacing(1) },
  hideZoneButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: "50%",
    transform: "translate(0, -50%)",
    visibility: "hidden",
  },
  selectedElement: { background: theme.palette.background.selected },
  hiddenElement: { opacity: 0.3 },
  layerItem: {
    width: "100%",
    boxShadow: "none",
    borderBottom: `1px solid ${theme.palette.background.sidebarSelected}`,
    "&:first-child": {
      borderRadius: 0,
      borderTop: `1px solid ${theme.palette.background.sidebarSelected}`,
    },
    "&:last-child": { borderBottom: "none" },
    "&:hover $iconsWrapper": { visibility: "visible" },
  },
  layerHeader: {
    position: "relative",
    padding: `0 0 0 ${theme.spacing(1)}px`,
    "& > div": {
      margin: "0 !important",
      overflow: "hidden",
    },
    "& > div:last-child": { padding: `${theme.spacing(1.5)}px ${theme.spacing(1)}px` },
  },
  layerTitle: {
    display: "block",
    minWidth: 0,
    textTransform: "none",
    "& > span": { ...overflowTextStyle },
  },
  layerContent: {
    display: "block",
    padding: `${theme.spacing(1)}px 0 ${theme.spacing(2)}px 0`,
  },
  layerEmptyContent: {
    paddingLeft: theme.spacing(2),
  },
  layerZone: {
    display: "block",
    maxWidth: `calc(100% - ${theme.spacing(6)}px)`,
    padding: theme.spacing(0.75),
    textAlign: "left",
    textTransform: "none",
    "& > span": { ...overflowTextStyle },
  },
  layerZoneWithoutCalendar: {
    marginLeft: theme.spacing(3.5),
  },
  calendarIcon: {
    marginTop: theme.spacing(0.75),
    marginLeft: theme.spacing(0.75),
  },
  zoneContainer: {
    position: "relative",
    "&:hover $hideZoneButton": { visibility: "visible" },
  },
  persistedStatus: {
    position: "absolute",
    zIndex: 99,
    width: theme.spacing(0.75),
    height: theme.spacing(0.75),
    left: theme.spacing(1),
    top: "50%",
    borderRadius: "50%",
    background: theme.palette.text.blue,
    transform: "translate(0, -50%)",
  },
  persistedLayer: { left: -theme.spacing(1.25) },
}));

const LayersListPanel = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();

  const { billing } = useContext(FleetSettingsContext);

  const {
    name,
    showPolygons,
    showHexes,
    sidewalksEditMode,
    layers,
    selectedLayer,
    selectedZone,
    setName,
    setVisibilityOptions,
    addLayer,
    updateLayer,
    updateZone,
    selectLayer,
    selectZone,
  } = useContext(GeofencesContext);

  const sortedLayers = Object.values(layers)
    .sort((l1, l2) => {
      if (l1.type === GeofencesLayerTypes.none) return -1;
      if (l2.type === GeofencesLayerTypes.none) return 1;

      const l1Type = t(`Fleet.Geofences.LayerTypes.${l1.type}`);
      const l2Type = t(`Fleet.Geofences.LayerTypes.${l2.type}`);

      if (l1Type < l2Type) return -1;
      if (l1Type > l2Type) return 1;

      return 0;
    })
    .map((layer) => ({
      ...layer,
      persisted: !Object.values(layer.zones).some((zone) => !zone.persisted),
    }));

  const editMode = selectedLayer.editMode || selectedZone.editMode || sidewalksEditMode;
  const isEditingPolygons =
    editMode === GeofencesEditModes.polygonCreate || editMode === GeofencesEditModes.polygonEdit;
  const isEditingHexes = editMode === GeofencesEditModes.hex;

  const [nameEditing, setNameEditing] = useState(false);
  const [filename, setFileName] = useState(name.value);

  const mapVisibilityOptions = [showPolygons && "polygons", showHexes && "hexes"].filter(Boolean);

  const onMapVisibilityChange = (e, visibilityOptions) => {
    setVisibilityOptions(
      visibilityOptions.includes("polygons"),
      visibilityOptions.includes("hexes"),
    );
  };

  const onChangeNameEditing = (event) => {
    event.preventDefault();

    if (nameEditing) {
      setName({ name: filename });
    }
    setNameEditing(!nameEditing);
  };

  const onShowHideLayer = (event, layer) => {
    event.stopPropagation();
    updateLayer(layer.key, { hidden: !layer.hidden });
  };

  const onShowHideZone = (event, zone, parent, preservePersistedStatus = true) => {
    event.stopPropagation();
    updateZone(zone.key, parent, { hidden: !zone.hidden }, preservePersistedStatus);
  };

  const onSelectLayer = (event, key) => {
    event.stopPropagation();
    selectLayer(selectedLayer.key === key ? null : key);
  };

  const onSelectZone = (key, parent) => {
    const newKey = selectedZone.key === key ? null : key;
    const newParent = selectedZone.key === key ? null : parent;

    selectZone({ key: newKey, parent: newParent });
  };

  const isTimedZone = (zone) => !!zone.rules?.find((rule) => rule.start_time);

  return (
    <FloatingPanel position={GeofencesPanelPositions.LEFT}>
      <Grid container>
        <form onSubmit={onChangeNameEditing} className={classes.titleWrapper}>
          <div className={clsx(classes.titleEditor, nameEditing && classes.isEditing)}>
            <h3 className={classes.overflowText}>{name.value}</h3>
            {name.editable && (
              <FilledTextField
                value={filename}
                type="text"
                onChange={(e) => setFileName(e.target.value)}
                underlineColor="white"
              />
            )}
          </div>
          {name.editable && (
            <IconButton size="small" onClick={onChangeNameEditing}>
              {nameEditing ? <DoneIcon /> : <EditIcon />}
            </IconButton>
          )}
        </form>
        <ToggleButtonGroup
          value={mapVisibilityOptions}
          size="small"
          onChange={onMapVisibilityChange}
          className={classes.toggleButtonGroup}
        >
          <ToggleButton value="polygons" disabled={isEditingPolygons}>
            {showPolygons ? (
              <VisibilityOffIcon className={classes.buttonIcon} />
            ) : (
              <VisibilityIcon className={classes.buttonIcon} />
            )}
            <span>Polygons</span>
          </ToggleButton>
          <ToggleButton value="hexes" disabled={isEditingHexes}>
            {showHexes ? (
              <VisibilityOffIcon className={classes.buttonIcon} />
            ) : (
              <VisibilityIcon className={classes.buttonIcon} />
            )}
            <span>Hexes</span>
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          className={classes.addButton}
          onClick={() => addLayer()}
          disabled={isEditingPolygons}
        >
          <AddCircleIcon className={classes.buttonIcon} />
          {t("Fleet.Geofences.Buttons.NewLayer")}
        </Button>
        <Grid container data-testid="layers-list">
          {sortedLayers.map((layer) => (
            <Accordion key={layer.key} className={classes.layerItem} style={{ margin: 0 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label="Expand"
                aria-controls="additional-actions1-content"
                className={clsx({
                  [classes.layerHeader]: true,
                  [classes.hiddenElement]: layer.hidden,
                  [classes.selectedElement]: selectedLayer.key === layer.key,
                })}
                style={{ minHeight: "unset" }}
              >
                {!layer.persisted && (
                  <span className={clsx([classes.persistedStatus, classes.persistedLayer])} />
                )}
                <LayerWithType type={layer.type}>
                  <Button
                    className={classes.layerTitle}
                    onClick={(event) => onSelectLayer(event, layer.key)}
                  >
                    {(layer.name || t(`Fleet.Geofences.LayerTypes.${layer.type}`)) +
                      (layer.speed ? `, ${layer.speed} km/h` : "") +
                      (layer.incentive?.amount
                        ? `, ${parseInt(layer.incentive.amount, 10) / 100} ${
                            layer.incentive.currency || billing.currency
                          }`
                        : "")}
                  </Button>
                  <div className={classes.iconsWrapper}>
                    <IconButton size="small" onClick={(event) => onShowHideLayer(event, layer)}>
                      {layer.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </div>
                </LayerWithType>
              </AccordionSummary>
              <AccordionDetails
                className={clsx({
                  [classes.layerContent]: true,
                  [classes.hidden]: layer.hidden,
                })}
              >
                {Object.values(layer.zones).length === 0 && (
                  <div className={classes.layerEmptyContent}>
                    {t("Fleet.Geofences.EmptyLayerNotice")}
                  </div>
                )}
                {_.orderBy(Object.values(layer.zones), ["created"], ["desc"]).map((zone) => (
                  <div key={zone.key} className={classes.zoneContainer}>
                    {!zone.persisted && <span className={classes.persistedStatus} />}
                    <div style={{ display: "flex" }}>
                      {isTimedZone(zone) && (
                        <span className={classes.calendarIcon}>
                          {" "}
                          <DateRangeIcon />{" "}
                        </span>
                      )}
                      <Button
                        className={clsx({
                          [classes.layerZone]: true,
                          [classes.layerZoneWithoutCalendar]: !isTimedZone(zone),
                          [classes.hiddenElement]: !zone.active || zone.hidden,
                          [classes.selectedElement]: selectedZone && selectedZone.key === zone.key,
                        })}
                        onClick={() => onSelectZone(zone.key, layer.key)}
                      >
                        {zone.name || t("Fleet.Geofences.ZoneName")}
                      </Button>
                    </div>
                    <IconButton
                      size="small"
                      className={classes.hideZoneButton}
                      onClick={(event) => onShowHideZone(event, zone, layer.key)}
                    >
                      {zone.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </div>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>
    </FloatingPanel>
  );
};

export default LayersListPanel;
